import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';

import { useHasChangedShallow } from '$/hooks/useHasChanged';

import ToggleLeftRight from '../ui/Input/Basics/ToggleLeftRight';
import CountryInput from '../ui/Input/Location/CountryInput';
import ProvinceInput from '../ui/Input/Location/ProvinceInput';
import StateInput from '../ui/Input/Location/StateInput';
import StripeCard from '../ui/Input/StripeCard';
import { navigate } from '@reach/router';
import Creds from '../ui/Creds';
import Form from '../ui/Form';
import Input from '../ui/Input/Basics/Input';

export const BillingAddressAndCC: React.FC<{
  update?: boolean;
  hideCardInput?: boolean;
  showBillingCycleSwitch?: boolean;
  onCardChange?: (event: StripeCardElementChangeEvent) => void;
}> = ({ update, hideCardInput, showBillingCycleSwitch, onCardChange }) => {
  const { setValue, watch } = useFormContext();
  const country = watch('team.address.country');
  const isAnnual = watch('isAnnual');
  let ProvinceControl = null;
  let placeholder = '';
  let zipcodePlaceholder = 'Postal code';

  switch (country) {
    case 'US':
      ProvinceControl = StateInput;
      placeholder = 'Select State';
      zipcodePlaceholder = 'Zip code';
      break;
    case 'CA':
      ProvinceControl = ProvinceInput;
      placeholder = 'Select Province';
      break;
    default:
      ProvinceControl = Input;
      placeholder = 'Enter Province';
      break;
  }

  const countryChanged = useHasChangedShallow([country], { skipInitial: true });
  React.useEffect(() => {
    if (!countryChanged) return;
    setValue('team.address.postal', '', { shouldDirty: true });
  }, [country, countryChanged, setValue]);

  const isAnnualChanged = useHasChangedShallow([isAnnual], {
    skipInitial: true,
  });
  React.useEffect(() => {
    if (!isAnnualChanged) return;

    const url = new URL(location.href);
    if (isAnnual) {
      url.searchParams.set('annual', '1');
    } else {
      url.searchParams.delete('annual');
    }
    navigate(url.toString(), { replace: true });
  }, [isAnnual, isAnnualChanged]);

  return (
    <>
      {!hideCardInput && (
        <>
          <Creds.Subtitle className='mx-2'>
            {update ? 'Update your payment details' : 'Your payment details'}
          </Creds.Subtitle>
          <Creds.Note className='pl-2'>
            Your credit card will only be charged after the trial period ends. We’ll send you two
            emails to remind you of the trial end date and you can cancel any time.
          </Creds.Note>
          <div
            className='flex flex-wrap align-center px-2 -mx-2 mb-2 '
            css={css`
              width: calc(100% + 1rem);
            `}
          >
            <Form.Field
              name='card'
              className='w-full'
              onChange={onCardChange}
              component={<StripeCard placeholder='16 digit card number' />}
            />
          </div>
          {showBillingCycleSwitch && (
            <Form.Field
              name='isAnnual'
              className='w-full'
              component={
                <ToggleLeftRight
                  css={css`
                    margin: auto;
                    .right,
                    .left {
                      ${tw`font-bold text-md`}
                    }
                    input:checked + .switch {
                      ${tw`bg-gray-1`}
                    }
                  `}
                  value={isAnnual}
                  leftLabel='Monthly'
                  rightLabel={<span>Annual (15% off)</span>}
                />
              }
            />
          )}
          <Creds.Separator />
        </>
      )}

      <Creds.Subtitle className='mx-2'>Your billing address</Creds.Subtitle>
      <div className={`flex flex-wrap px-2 -mx-2`}>
        <Form.Field
          name='team.address.company'
          component={<Input autoComplete='company' placeholder='Company' />}
        />
        <Form.Field
          name='team.address.address1'
          component={<Input autoComplete='address-line1' placeholder='Address' />}
        />
        <Form.Field
          name='team.address.city'
          component={<Input autoComplete='address-level2' placeholder='City' />}
        />
        <Form.Field
          component={
            <CountryInput
              id='countryInput'
              autoComplete='country-name'
              placeholder='Your country'
            />
          }
          name='team.address.country'
        />
        <Form.Field
          name='team.address.province'
          component={
            <ProvinceControl
              id='stateInput'
              placeholder={placeholder}
              autoComplete='address-level1'
            />
          }
        />
        <Form.Field
          name='team.address.postal'
          component={<Input autoComplete='postal-code' placeholder={zipcodePlaceholder} />}
        />
      </div>
    </>
  );
};
